import React, { useMemo, useRef, useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Table, Tooltip } from "src/assets/imports/antdComponents";
import type { ColumnsType } from "antd/es/table";
import {
	getHClusterBarChartColumns,
	getHClusterBarChartTableData,
	getVStackedBarChartColumns,
	getVStackedBarChartTableData,
	getHStackedBarChartColumns,
	getHStackedBarChartTableData,
	formatValue,
	ColumnConfig,
	TableRow,
} from "./chartTableTransformer";
import { tableConfig, StatMarkerPositionType, ITableYAxisWidthConfig, TABLE_ATTR_MULTI_TILE_DEFAULTS, TABLE_ATTR_SINGLE_DEFAULTS } from "../tableConfig";
import "./chartTableStyle.css";
import { ChartRenderedFromType, IHierarchyDef, IStatMarker, IStatTestConfig } from "src/pages/Insights/models/interfaces";
import { resolveMarkerSymbol, resolveStatMarkerRows } from "./StatMarkerUtils";
import { measureTextWidth, getNSizeStatusLabel, NSizeLabelText, SUPPRESSED_VALUE_COLOR, SUPPRESSED_VALUE_PLACEHOLDER } from "../BarCharts/chartHelpers";
import { pxToRem } from "src/pages/Insights/transformer/dashboardUtils";

/* =====================================
	 TYPE DEFINITIONS
	 ===================================== */

/**
 * Cell properties interface for proper TypeScript typing
 */
interface CellProps {
	"data-column-type": "attribute" | "wave" | "numerical";
	"data-last-attribute-row"?: "true";
	className?: string;
	rowSpan?: number;
}

/**
 * Header cell properties interface
 */
interface HeaderCellProps {
	"data-column-type": "attribute" | "wave" | "numerical";
	className?: string;
	rowSpan?: number;
}

/* =====================================
	 UTILITY FUNCTIONS - Module Level
	 ===================================== */

/**
 * Debounce utility function - module level to avoid recreation
 */
function debounce<T extends (...args: any[]) => any>(func: T, delay: number): ((...args: Parameters<T>) => void) & { cancel: () => void } {
	let timeoutId: NodeJS.Timeout | null = null;

	const debouncedFunction = (...args: Parameters<T>) => {
		if (timeoutId) {
			clearTimeout(timeoutId);
		}

		timeoutId = setTimeout(() => {
			func(...args);
		}, delay);
	};

	debouncedFunction.cancel = () => {
		if (timeoutId) {
			clearTimeout(timeoutId);
			timeoutId = null;
		}
	};

	return debouncedFunction;
}

/* =====================================
	 WAVE NAMES EXTRACTION FROM DATA SOURCE
	 ===================================== */

/**
 * Extract unique wave names from the already-computed dataSource rows.
 *
 * Why this is better than extractWaveNames(chartData):
 *   extractWaveNames reads series[].name from the raw chart data.
 *   For h_cluster_bar / h_stacked_bar these are the wave labels (correct).
 *   For v_stacked_column / v_cluster_column the "wave" column is either empty
 *   or holds xAxis values — series names would be wrong.
 *   row.wave is always the correct rendered label regardless of chart type,
 *   so reading from dataSource is orientation-agnostic and always correct.
 */
const extractWaveNamesFromDataSource = (dataSource: TableRow[]): string[] => {
	const seen = new Set<string>();
	for (const row of dataSource) {
		const waveName = row.wave;
		if (typeof waveName === "string" && waveName.trim() !== "" && !seen.has(waveName)) {
			seen.add(waveName);
		}
	}
	return Array.from(seen);
};

/* =====================================
	 TABLE ELLIPSIS TEXT - Internal Component
	 ===================================== */
interface TableEllipsisTextProps {
	text: string;
	maxLines?: number;
	lineHeight?: number;
	className?: string;
	style?: React.CSSProperties;
}

const TableEllipsisText: React.FC<TableEllipsisTextProps> = ({ text, maxLines = 4, lineHeight = 14, className = "", style = {} }) => {
	const textRef = useRef<HTMLSpanElement>(null);
	const [isTruncated, setIsTruncated] = useState(false);

	const checkTruncation = useCallback(() => {
		const element = textRef.current;
		if (element) {
			const isOverflowing = element.scrollHeight > element.clientHeight;
			setIsTruncated(isOverflowing);
		}
	}, []);

	useEffect(() => {
		checkTruncation();
		window.addEventListener("resize", checkTruncation);
		return () => window.removeEventListener("resize", checkTruncation);
	}, [checkTruncation]);

	const textElement = (
		<span
			ref={textRef}
			className={`overflow-hidden w-full ${className}`}
			style={{
				display: "-webkit-box",
				WebkitLineClamp: maxLines,
				WebkitBoxOrient: "vertical",
				textOverflow: "ellipsis",
				lineHeight: `${lineHeight}px`,
				wordWrap: "break-word",
				overflowWrap: "break-word",
				...style,
				padding: "0",
			}}>
			{text}
		</span>
	);

	if (!isTruncated) {
		return textElement;
	}

	return (
		<Tooltip title={text} placement="top" overlayStyle={{ maxWidth: 400 }} getPopupContainer={(triggerNode) => triggerNode.closest(".ant-table-container") || document.body}>
			{textElement}
		</Tooltip>
	);
};

/* =====================================
	 N-SIZE DISPLAY COMPONENT - With Truncation
	 ===================================== */
interface NSizeDisplayProps {
	minValue: number | string;
	maxValue: number | string;
	minColor: string;
	maxColor: string;
	typographyConfig: any;
	/** Override font size from scaling system (px). When undefined, uses tableConfig default. */
	scaledFontSize?: number;
}

const NSizeDisplay: React.FC<NSizeDisplayProps> = ({ minValue, maxValue, minColor, maxColor, typographyConfig, scaledFontSize }) => {
	const textRef = useRef<HTMLDivElement>(null);
	const [isTruncated, setIsTruncated] = useState(false);

	const checkTruncation = useCallback(() => {
		const element = textRef.current;
		if (element) {
			const isOverflowing = element.scrollWidth > element.clientWidth || element.scrollHeight > element.clientHeight;
			setIsTruncated(isOverflowing);
		}
	}, []);

	useEffect(() => {
		checkTruncation();
		window.addEventListener("resize", checkTruncation);
		return () => window.removeEventListener("resize", checkTruncation);
	}, [minValue, maxValue, checkTruncation]);

	const displayText = minValue === maxValue ? `n = ${minValue}` : `n = ${minValue} - ${maxValue}`;

	// Get n-size row style from config
	const nSizeStyle = tableConfig.getNSizeRowStyle();

	const content = (
		<div
			ref={textRef}
			className="n-size-display"
			style={{
				fontSize: scaledFontSize ? `${scaledFontSize}px` : `${nSizeStyle.fontSize}px`,
				fontWeight: nSizeStyle.fontWeight,
				color: typographyConfig.color.tertiary,
				textAlign: "center",
				lineHeight: `${nSizeStyle.lineHeight}px`,
				wordWrap: "break-word",
				overflowWrap: "break-word",
				padding: "0",
			}}>
			{minValue === maxValue ? (
				<>
					n ={" "}
					<span className="n-size-value" style={{ color: minColor }}>
						{minValue}
					</span>
				</>
			) : (
				<>
					n ={" "}
					<span className="n-size-value" style={{ color: minColor }}>
						{minValue}
					</span>
					{" - "}
					<span className="n-size-value" style={{ color: maxColor }}>
						{maxValue}
					</span>
				</>
			)}
		</div>
	);

	if (!isTruncated) {
		return content;
	}

	return (
		<Tooltip
			title={displayText}
			placement="top"
			overlayStyle={{ maxWidth: 200 }}
			getPopupContainer={(triggerNode) => triggerNode.closest(".ant-table-container") || document.body}>
			{content}
		</Tooltip>
	);
};

/* =====================================
	 STAT MARKER DISPLAY - Config-driven positioning
	 ===================================== */

interface IStatMarkerDisplayProps {
	value: React.ReactNode;
	statMarkerObj: IStatMarker;
	statTestConfig: IStatTestConfig;
	position: StatMarkerPositionType;
	spacing: number;
	maxLines: number;
	maxWidth: string;
}

const StatMarkerDisplay: React.FC<IStatMarkerDisplayProps> = ({ value, statMarkerObj, statTestConfig, position, spacing, maxLines, maxWidth }) => {
	const markerRef = useRef<HTMLSpanElement>(null);
	const [isMarkerTruncated, setIsMarkerTruncated] = useState(false);

	const resolvedRows = useMemo(() => resolveStatMarkerRows(statMarkerObj, statTestConfig), [statMarkerObj, statTestConfig]);

	const displayText = useMemo(() => resolvedRows.map((r) => r.displayText).join(","), [resolvedRows]);

	const isVertical = position === StatMarkerPositionType.Top || position === StatMarkerPositionType.Bottom;
	const isReversed = position === StatMarkerPositionType.Top || position === StatMarkerPositionType.Left;

	const checkMarkerTruncation = useCallback(() => {
		const el = markerRef.current;
		if (el) {
			const isTrunc = maxLines === 1 ? el.scrollWidth > el.clientWidth : el.scrollHeight > el.clientHeight;
			setIsMarkerTruncated(isTrunc);
		}
	}, [maxLines]);

	useEffect(() => {
		const timer = setTimeout(checkMarkerTruncation, 0);
		window.addEventListener("resize", checkMarkerTruncation);
		return () => {
			clearTimeout(timer);
			window.removeEventListener("resize", checkMarkerTruncation);
		};
	}, [displayText, checkMarkerTruncation]);

	if (resolvedRows.length === 0) return <>{value}</>;

	const baseStyle = resolvedRows[0].style;
	const markerStyle: React.CSSProperties = {
		fontSize: `${baseStyle.font_size ?? 10}px`,
		fontWeight: baseStyle.font_weight,
		color: baseStyle.font_color,
		lineHeight: 1,
	};

	const truncationStyle: React.CSSProperties =
		maxLines === 1
			? { ...markerStyle, display: "block", maxWidth, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }
			: {
				...markerStyle,
				display: "-webkit-box",
				WebkitLineClamp: maxLines,
				WebkitBoxOrient: "vertical",
				overflow: "hidden",
				textOverflow: "ellipsis",
				maxWidth,
				whiteSpace: "normal",
				wordBreak: "break-all",
			};

	const tooltipContent = (
		<div style={{ fontFamily: "inherit", minWidth: "fit-content" }}>
			<div style={{ fontSize: 9, color: "#6B7280", fontWeight: 600, marginBottom: 2 }}>Stat Markers</div>
			{resolvedRows.map((row, rIdx) => (
				<div className="flex items-center justify-center gap-2" key={rIdx} style={{ marginBottom: rIdx < resolvedRows.length - 1 ? 4 : 0 }}>
					<div style={{ fontSize: 9, color: "#9CA3AF" }}>{row.ciLevel}% CI:</div>
					<div style={{ fontSize: 10, color: "#1F2937" }}>
						{row.items.map((item, iIdx) => {
							const symbol = resolveMarkerSymbol(item.marker_label);
							const directionIcon = item.direction === "higher" ? "↑" : item.direction === "lower" ? "↓" : "";
							const pVal = statTestConfig.show_p_value && item.p_value != null ? ` (p=${item.p_value.toFixed(4)})` : "";
							return (
								<span key={iIdx}>
									{iIdx > 0 && ","}
									<span style={{ color: row.style.font_color, fontWeight: row.style.font_weight }}>{symbol}</span>
									{directionIcon}
									<span style={{ color: "#9CA3AF" }}>{pVal}</span>
								</span>
							);
						})}
					</div>
				</div>
			))}
		</div>
	);

	const markerElement = (
		<span ref={markerRef} style={truncationStyle}>
			{displayText}
		</span>
	);

	const wrappedMarker = (
		<Tooltip
			title={tooltipContent}
			placement="right"
			overlayStyle={{ maxWidth: 300 }}
			overlayInnerStyle={{ maxHeight: "20rem", overflowY: "auto" }}
			getPopupContainer={(triggerNode) => triggerNode.closest(".ant-table-body") || document.body}>
			{markerElement}
		</Tooltip>
	);

	return (
		<span
			style={{
				display: "flex",
				flexDirection: isVertical ? "column" : "row",
				alignItems: "center",
				justifyContent: "center",
				gap: `${spacing}px`,
			}}>
			{isReversed && wrappedMarker}
			{value}
			{!isReversed && wrappedMarker}
		</span>
	);
};

/* =====================================
	 MAIN COMPONENT
	 ===================================== */
export interface IScaledTableOverrides {
	dataFontSize: number;
	headerFontSize: number;
	nSizeFontSize: number;
	cellPaddingV: number;
	cellPaddingH: number;
	/** Max lines for ellipsis text — reduced in small tiles */
	maxEllipsisLines: number;
	scaleFactor?: number;
	/** Whether this is rendering in multi-tile mode */
	isMultiTile?: boolean;
}
interface ChartTabularViewProps {
	chartData: any;
	attributes?: string[];
	chartType?: string;
	tableContainerHeight?: number;
	parentContainerWidth?: number;
	labelFormat?: string;
	statTestConfig?: IStatTestConfig | null;
	renderedFrom?: string;
	/** When provided, overrides tableConfig typography/spacing for multi-tile rendering */
	scaledOverrides?: IScaledTableOverrides;
	filterHierarchy?: IHierarchyDef[]; // Filter hierarchy definition, used to preserve wave/row ordering in tabular view
	seriesOrder?: string[];
}

/* =====================================
	 COLUMN WIDTH HELPERS
	 ===================================== */

/**
 * Calculate wave column width based on longest wave/series name text.
 * Mirrors the same logic used for the attribute column.
 */
const calculateWaveColumnWidth = (waveNames: string[]): number => {
	const config = tableConfig.getConfig();
	const typographyConfig = config.typography;
	const waveConfig = config.columns.wave;

	if (!waveNames || waveNames.length === 0) {
		return waveConfig.minWidth;
	}

	const maxTextLength = Math.max(...waveNames.map((w) => w.length));
	const baseWidth = maxTextLength * typographyConfig.charWidth + 32; // 32px for padding

	return Math.min(Math.max(baseWidth, waveConfig.minWidth), waveConfig.maxWidth);
};
/* =====================================
	 ATTRIBUTE COLUMN WIDTH — Hybrid approach
	 Content-driven px + container-relative constraint
	 ===================================== */

/**
 * Estimate text width when canvas measurement is unavailable (SSR)
 */
function estimateTextWidth(text: string, charWidth: number): number {
	return text.length * charWidth;
}

interface IComputeTableAttrWidthParams {
	attributes: string[];
	containerWidth: number;
	isColumnChart?: boolean;
	isMultiTile?: boolean;
	scaleFactor?: number;
	config?: Partial<ITableYAxisWidthConfig>;
}

interface ITableAttrWidthResult {
	width: number;
	willTruncate: boolean;
}

/**
 * Calculate attribute column width using hybrid approach:
 * 1. Measure actual text content via canvas
 * 2. Add config-driven padding
 * 3. Clamp between absolute floor and min(ceilingPx, containerWidth * maxContainerFraction)
 *
 * This prevents the attribute column from consuming too much space in small
 * containers (multi-tile) while fitting content accurately in single-chart mode.
 */
const computeTableAttributeWidth = ({
	attributes,
	containerWidth,
	isColumnChart = false,
	isMultiTile = false,
	scaleFactor = 1,
	config: configOverride,
}: IComputeTableAttrWidthParams): ITableAttrWidthResult => {
	const defaults = isMultiTile ? TABLE_ATTR_MULTI_TILE_DEFAULTS : TABLE_ATTR_SINGLE_DEFAULTS;
	const cfg: ITableYAxisWidthConfig = { ...defaults, ...configOverride };

	const typoCfg = tableConfig.getTypographyConfig();

	// Use column-chart floor if applicable
	const effectiveFloorPx = isColumnChart ? Math.max(cfg.floorPx, tableConfig.getAttributeMinWidth(true)) : cfg.floorPx;

	// Step 1: Measure content need via canvas
	const fontSize = isMultiTile ? Math.max(8, Math.round(typoCfg.fontSize.data * scaleFactor)) : typoCfg.fontSize.data;
	const fontWeight = typoCfg.fontWeight.attribute;
	const font = `${fontWeight} ${fontSize}px "Open Sans", sans-serif`;

	let maxTextWidth = 0;
	if (attributes.length > 0) {
		// Only measure the top 5 longest strings to keep it efficient
		const sorted = [...attributes].sort((a, b) => b.length - a.length);
		const toMeasure = sorted.slice(0, 5);

		for (const attr of toMeasure) {
			const measured = measureTextWidth(attr, font);
			const width = measured > 0 ? measured : estimateTextWidth(attr, typoCfg.charWidth);
			if (width > maxTextWidth) maxTextWidth = width;
		}
	}

	const idealWidth = maxTextWidth + cfg.paddingPx;

	// Step 2: Container-relative ceiling
	const containerCeiling = containerWidth > 0 ? Math.floor(containerWidth * cfg.maxContainerFraction) : cfg.ceilingPx;

	// Step 3: Three-way clamp
	const effectiveCeiling = Math.min(cfg.ceilingPx, containerCeiling);
	const clampedFloor = Math.min(effectiveFloorPx, effectiveCeiling);

	const contentBased = Math.max(idealWidth, cfg.minContentPx);
	const width = Math.round(Math.min(Math.max(contentBased, clampedFloor), effectiveCeiling));

	return { width, willTruncate: idealWidth > width };
};

/**
 * Original signature preserved for backward compatibility.
 * Used by tableDimensions calculation where containerWidth isn't yet available.
 */
const calculateAttributeColumnWidth = (
	attributes: string[],
	isColumnChart: boolean = false,
	containerWidth: number = 0,
	isMultiTile: boolean = false,
	scaleFactor: number = 1,
): number => {
	const { width } = computeTableAttributeWidth({
		attributes,
		containerWidth,
		isColumnChart,
		isMultiTile,
		scaleFactor,
	});
	return width;
};
/**
 * Calculate attribute column width based on longest text (similar to y-axis calculation)
 * For column-type charts (stacked/cluster column), use larger min width from config
 */
// const calculateAttributeColumnWidth = (attributes: string[], isColumnChart: boolean = false): number => {
// 	const config = tableConfig.getConfig();
// 	const typographyConfig = config.typography;
// 	const attributeConfig = config.columns.attribute;

// 	// Get min width based on chart type (config-driven)
// 	const minWidth = tableConfig.getAttributeMinWidth(isColumnChart);

// 	if (!attributes || attributes.length === 0) {
// 		return minWidth;
// 	}

// 	// Find the longest attribute text
// 	const maxTextLength = Math.max(...attributes.map((attr) => attr.length));

// 	// Calculate width based on character width + padding
// 	const baseWidth = maxTextLength * typographyConfig.charWidth + 32; // 32px for padding

// 	// Ensure it's within min/max bounds
// 	return Math.min(Math.max(baseWidth, minWidth), attributeConfig.maxWidth);
// };

/* =====================================
	 CONTENT-DRIVEN COLUMN WIDTH HELPERS
	 ===================================== */

const extractLeafTitlesAboveNSize = (columns: any[]): string[] => {
	const titles: string[] = [];

	const traverse = (cols: any[]) => {
		cols.forEach((col) => {
			if (col._hasNSizeRow && typeof col.title === "string") {
				// This node is the direct parent of the n-size cell — its title is
				// the column label that sits above the n-size row.
				titles.push(col.title);
			} else if (col.children && col.children.length > 0) {
				traverse(col.children);
			}
		});
	};

	traverse(columns);
	return titles;
};

// Font metrics used to estimate rendered text width for the column header:
// font-size 12px, font-weight 600, 8px horizontal padding on each side.
const CONTENT_CHAR_WIDTH_PX = 7.2; // average glyph width at 12px / weight-600
const CONTENT_CELL_H_PADDING = 16; // 8px left + 8px right
const CONTENT_COL_MIN_WIDTH = 60; // absolute floor for a numerical column

/**
 * Return the pixel width needed to display the longest title above the n-size
 * row without truncation.
 */
const calculateNumericalWidthFromContent = (titlesAboveNSize: string[]): number => {
	if (!titlesAboveNSize || titlesAboveNSize.length === 0) {
		return CONTENT_COL_MIN_WIDTH;
	}
	const maxLength = Math.max(...titlesAboveNSize.map((t) => t.length));
	const computed = Math.ceil(maxLength * CONTENT_CHAR_WIDTH_PX) + CONTENT_CELL_H_PADDING;
	const numericColMaxWidth = tableConfig.getNumericColumnMaxWidth();
	const finalNumericColWidth = Math.min(computed, numericColMaxWidth);
	return Math.max(finalNumericColWidth, CONTENT_COL_MIN_WIDTH);
};

/* =====================================
	 FALLBACK WIDTH RESOLVER
	 ===================================== */

interface FallbackWidthResult {
	numericalColumnWidth: number;
	wrapperWidth: string;
	scrollX: number;
}

/**
 * Resolves numerical column width using a cascade of fallback strategies
 * when the content-driven candidate width would overflow the container.
 *
 * Strategy:
 *   1. Try candidate (content-driven or proportional) — fits → done.
 *   2. If overflow → try fallback1 (fewer visible columns).
 *   3. If still overflow → try fallback2 (even fewer visible columns).
 *   4. If still overflow → revert to proportional sizing and allow scroll.
 *
 * Pure function with explicit inputs and a single return value — no side effects.
 */
const resolveFallbackWidth = (
	originalCalculatedColumnWidth: number,
	candidateWidth: number,
	numericalColumnCount: number,
	fixedColumnsWidth: number,
	availableWidth: number,
	waveWidth: number,
	effectiveContainerWidth: number,
	fallback1TargetVisibleColumns: number,
	fallback2TargetVisibleColumns: number,
	totalWidth: number,
): FallbackWidthResult => {
	const projectedTotal = fixedColumnsWidth + candidateWidth * numericalColumnCount;

	// Candidate fits — use it directly.
	if (projectedTotal <= effectiveContainerWidth) {
		return {
			numericalColumnWidth: candidateWidth,
			wrapperWidth: `${projectedTotal + 13}px`,
			scrollX: projectedTotal,
		};
	}

	// Fallback 1: divide remaining space across fewer visible columns.
	const fallback1Width = Math.max((availableWidth - waveWidth) / fallback1TargetVisibleColumns, 50);
	const fallback1Total = fixedColumnsWidth + fallback1Width * numericalColumnCount;

	if (fallback1Total < effectiveContainerWidth) {
		return {
			numericalColumnWidth: fallback1Width,
			wrapperWidth: `${fallback1Total + 13}px`,
			scrollX: fallback1Total,
		};
	}

	// Fallback 2: divide remaining space across even fewer visible columns.
	const fallback2Width = Math.max((availableWidth - waveWidth) / fallback2TargetVisibleColumns, 50);
	const fallback2Total = fixedColumnsWidth + fallback2Width * numericalColumnCount;

	if (fallback2Total < effectiveContainerWidth) {
		return {
			numericalColumnWidth: fallback2Width,
			wrapperWidth: `${fallback2Total + 13}px`,
			scrollX: fallback2Total,
		};
	}

	// All fallbacks exceeded — revert to originalCaluculated width.
	return {
		numericalColumnWidth: originalCalculatedColumnWidth,
		wrapperWidth: `${totalWidth + 13}px`,
		scrollX: totalWidth,
	};
};

/* =====================================
	 TABLE DIMENSION CALCULATION
	 ===================================== */

/**
 * Calculate table dimensions and column widths based on available viewport space.
 *
 * Wave column width is content-driven (longest wave/series name), exactly as
 * attribute column width is derived from the longest attribute label.
 * After the wave width is fixed the remaining space is divided among numerical
 * columns, so numerical columns are never over-squeezed by a wide wave column.
 *
 * Fallback cascade for few-column scenarios is delegated to resolveFallbackWidth,
 * which is a pure function with clear input/output contracts.
 */
const calculateTableDimensions = (
	totalColumns: number,
	hasWaveColumn: boolean,
	hasAttributeColumn: boolean,
	attributes: string[],
	waveNames: string[],
	targetVisibleColumns: number,
	containerWidth: number,
	parentContainerWidth: number,
	isColumnChart: boolean = false,
	columnTitlesAboveNSize: string[] = [],
	fallback1TargetVisibleColumns: number,
	fallback2TargetVisibleColumns: number,
	scaledOverrides?: IScaledTableOverrides,
): {
	wrapperWidth: string;
	columnWidths: { attribute: number; wave: number; numerical: number };
	scrollX: number | string;
} => {
	// Calculate attribute column width based on content and chart type
	// Inside calculateTableDimensions, the attributeWidth line becomes:
	const attributeWidth = hasAttributeColumn
		? calculateAttributeColumnWidth(attributes, isColumnChart, parentContainerWidth, scaledOverrides?.isMultiTile ?? false, scaledOverrides?.scaleFactor ?? 1)
		: 0;
	// Calculate wave column width based on content (same logic as attribute)
	const contentDrivenWaveWidth = hasWaveColumn ? calculateWaveColumnWidth(waveNames) : 0;

	// Calculate numerical column count
	const numericalColumnCount = totalColumns - (hasAttributeColumn ? 1 : 0) - (hasWaveColumn ? 1 : 0);

	let waveWidth: number;
	let numericalColumnWidth: number;
	let availableWidth: number;

	if (containerWidth > 0 && numericalColumnCount > targetVisibleColumns) {
		// Available width after attribute column
		availableWidth = parentContainerWidth - attributeWidth;

		if (hasWaveColumn) {
			// Wave width: content-driven, but at least as wide as the proportional slot
			const slotWidth = availableWidth / (targetVisibleColumns + 1);
			waveWidth = Math.max(slotWidth, contentDrivenWaveWidth, 50);
			// Numerical columns share the remaining space after wave is sized
			numericalColumnWidth = Math.max((availableWidth - waveWidth) / targetVisibleColumns, 50);
		} else {
			numericalColumnWidth = Math.max(availableWidth / targetVisibleColumns, 50);
			waveWidth = 0;
		}
	} else {
		// Fallback when container width not available yet
		availableWidth = parentContainerWidth - attributeWidth;
		if (hasWaveColumn) {
			const slotWidth = availableWidth / (targetVisibleColumns + 1);
			waveWidth = Math.max(slotWidth, contentDrivenWaveWidth, 50);
			numericalColumnWidth = Math.max((availableWidth - waveWidth) / targetVisibleColumns, 50);
		} else {
			numericalColumnWidth = Math.max(availableWidth / targetVisibleColumns, 50);
			waveWidth = 0;
		}
	}

	// Calculate fixed columns width (attribute + wave)
	const fixedColumnsWidth = attributeWidth + waveWidth;

	// Calculate total width based on actual column count
	const totalWidth = fixedColumnsWidth + numericalColumnWidth * numericalColumnCount;

	// Total width to show TARGET columns + fixed columns (for scroll calculation)
	const totalVisibleWidth = fixedColumnsWidth + numericalColumnWidth * targetVisibleColumns;

	if (numericalColumnCount < targetVisibleColumns) {
		if (columnTitlesAboveNSize.length > 0) {
			// Content-driven width: pick the larger of content-driven and proportional
			// so columns never shrink below the proportional slot.
			const contentDrivenWidth = calculateNumericalWidthFromContent(columnTitlesAboveNSize);
			const candidateWidth = Math.max(contentDrivenWidth, numericalColumnWidth);
			const resolved = resolveFallbackWidth(
				numericalColumnWidth,
				candidateWidth,
				numericalColumnCount,
				fixedColumnsWidth,
				availableWidth,
				waveWidth,
				parentContainerWidth,
				fallback1TargetVisibleColumns,
				fallback2TargetVisibleColumns,
				totalWidth,
			);

			return {
				wrapperWidth: resolved.wrapperWidth,
				columnWidths: {
					attribute: attributeWidth,
					wave: waveWidth,
					numerical: resolved.numericalColumnWidth,
				},
				scrollX: resolved.scrollX,
			};
		} else {
			// No n-size titles (flat data) — original proportional fallback, no scroll.
			return {
				wrapperWidth: `${totalWidth + 13}px`,
				columnWidths: { attribute: attributeWidth, wave: waveWidth, numerical: numericalColumnWidth },
				scrollX: totalWidth,
			};
		}
	} else {
		// More columns than target — enable horizontal scroll.
		return {
			wrapperWidth: "100%",
			columnWidths: { attribute: attributeWidth, wave: waveWidth, numerical: numericalColumnWidth },
			scrollX: totalVisibleWidth,
		};
	}
};

/**
 * Helper function to create cell props with styling
 */
const createCellProps = (columnType: "attribute" | "wave" | "numerical", isEvenGroup?: boolean, record?: TableRow): CellProps => {
	const baseProps: CellProps = {
		"data-column-type": columnType,
	};

	// Add last attribute row flag if applicable
	if (record?.isLastAttributeRow && columnType === "attribute") {
		baseProps["data-last-attribute-row"] = "true";
	}

	if (isEvenGroup !== undefined) {
		return {
			...baseProps,
			className: isEvenGroup ? "even-column-group" : "odd-column-group",
		};
	}

	return baseProps;
};

/**
 * Helper function to create header cell props
 */
const createHeaderCellProps = (columnType: "attribute" | "wave" | "numerical", isEvenGroup?: boolean, rowSpan?: number): HeaderCellProps => {
	const baseProps: HeaderCellProps = {
		"data-column-type": columnType,
	};

	if (rowSpan && rowSpan > 1) {
		baseProps.rowSpan = rowSpan;
	}

	if (isEvenGroup !== undefined) {
		baseProps.className = isEvenGroup ? "even-column-group" : "odd-column-group";
	}

	return baseProps;
};

/**
 * Transform column config to Ant Design columns with table config applied
 * KEY: Handles 3-row header structure (Parent, Title, N-Size)
 */
const transformColumnsForAntd = (
	columns: ColumnConfig[],
	t: (key: string) => string,
	isStackedColumn: boolean = false,
	isClusterColumn: boolean = false,
	columnWidths?: { attribute: number; wave: number; numerical: number },
	labelFormat?: string,
	dataSource?: TableRow[],
	statTestConfig?: IStatTestConfig | null,
	scaledOverrides?: IScaledTableOverrides,
): ColumnsType<TableRow> => {
	// Get table configuration
	const config = tableConfig.getConfig();
	const typographyConfig = config.typography;
	const alignmentConfig = config.alignment;
	const spacingConfig = config.spacing;
	const eff = {
		dataFont: scaledOverrides?.dataFontSize ?? typographyConfig.fontSize.data,
		headerFont: scaledOverrides?.headerFontSize ?? typographyConfig.fontSize.header,
		nSizeFont: scaledOverrides?.nSizeFontSize ?? typographyConfig.headerHierarchy.nSizeRow.fontSize,
		padV: scaledOverrides?.cellPaddingV ?? spacingConfig.padding.cell.vertical,
		padH: scaledOverrides?.cellPaddingH ?? spacingConfig.padding.cell.horizontal,
		maxLines: scaledOverrides?.maxEllipsisLines ?? 4,
		lineHeight: Math.round((scaledOverrides?.dataFontSize ?? typographyConfig.fontSize.data) * 1.3),
		headerLineHeight: Math.round((scaledOverrides?.headerFontSize ?? typographyConfig.fontSize.header) * 1.3),
	};
	return columns
		.map((col) => {
			const baseColumn: any = {
				key: col.key,
				align: col.align,
				fixed: col.fixed,
			};

			// Determine column type for styling
			let columnType: "attribute" | "wave" | "numerical" = "numerical";
			if (col.dataIndex === "attribute") columnType = "attribute";
			else if (col.dataIndex === "wave") columnType = "wave";

			// Apply calculated width based on column type
			if (columnWidths) {
				if (columnType === "attribute") {
					baseColumn.width = columnWidths.attribute;
				} else if (columnType === "wave") {
					baseColumn.width = columnWidths.wave;
				} else {
					baseColumn.width = columnWidths.numerical;
				}
			} else {
				// Fallback to original calculation
				baseColumn.width = tableConfig.calculateColumnWidth(columnType, col.width);
			}

			// Check if this column group should have alternating background
			const isEvenGroup = (col as any)._isEvenGroup;

			// Apply cell props for header
			const headerCellProps = createHeaderCellProps(columnType, isEvenGroup);
			baseColumn.onHeaderCell = () => headerCellProps;

			// Special handling for attribute column to add data-last-attribute-row
			if (columnType === "attribute") {
				baseColumn.onCell = (record: TableRow, rowIndex?: number) => {
					const cellProps: CellProps = {
						"data-column-type": "attribute",
					};

					// Only add the flag if this is the first row of an attribute group (where cell renders)
					const rowSpan = record.attributeRowSpan ?? 1;

					if (rowSpan > 0 && dataSource && typeof rowIndex === "number" && rowIndex >= 0) {
						if (dataSource.length > 0) {
							// This is the first row of an attribute group where the cell renders
							// Calculate the index of the last row in this attribute group
							const lastRowIndex = rowIndex + rowSpan - 1;
							if (lastRowIndex >= 0 && lastRowIndex < dataSource.length) {
								const lastRowOfGroup = dataSource[lastRowIndex];
								if (lastRowOfGroup?.isLastAttributeRow) {
									cellProps["data-last-attribute-row"] = "true";
								}
							}
						}
					}

					return cellProps;
				};
			} else {
				// Apply cell props for body - pass record dynamically
				baseColumn.onCell = (record: TableRow) => {
					const cellPropsWithRecord = createCellProps(columnType, isEvenGroup, record);
					return cellPropsWithRecord;
				};
			}

			// Handle nested columns (hierarchy groups)
			if (col.children && col.children.length > 0) {
				const hierarchyLevel = (col as any)._hierarchyLevel ?? 0;
				const isLeafLevel = (col as any)._isLeafLevel ?? false;
				const headerStyle = tableConfig.getHeaderStyle(hierarchyLevel, isLeafLevel);
				const hasNSizeRow = (col as any)._hasNSizeRow;

				// CRITICAL: Check if this is n-size wrapper (parent contains title, child contains n-size)
				if (hasNSizeRow && col.children.length === 1) {
					const child = col.children[0];
					const nSizeData = (child as any)._nSizeData;

					// Parent title = Column name (Row 2: "Overall", "Demato Oncologist")
					baseColumn.title = (
						<TableEllipsisText
							text={t(col.title as string)}
							maxLines={eff.maxLines}
							lineHeight={eff.headerLineHeight}
							className="text-center"
							style={{ ...headerStyle, fontSize: `${pxToRem(eff.headerFont)}` }}
						/>
					);

					// Transform child
					const [transformedChild] = transformColumnsForAntd(
						[child],
						t,
						isStackedColumn,
						isClusterColumn,
						columnWidths,
						labelFormat,
						dataSource,
						statTestConfig,
						scaledOverrides,
					);

					// Replace child title with n-size display (Row 3: "n = 3")
					if (nSizeData) {
						const minColor = tableConfig.getNSizeColor(nSizeData.minValue);
						const maxColor = tableConfig.getNSizeColor(nSizeData.maxValue);

						transformedChild.title = (
							<NSizeDisplay
								minValue={nSizeData.minValue}
								maxValue={nSizeData.maxValue}
								minColor={minColor}
								maxColor={maxColor}
								typographyConfig={typographyConfig}
								scaledFontSize={scaledOverrides?.nSizeFontSize}
							/>
						);
					}

					baseColumn.children = [transformedChild];
				} else {
					// Normal parent with multiple children - process recursively
					baseColumn.title = (
						<TableEllipsisText
							text={t(col.title as string)}
							maxLines={eff.maxLines}
							lineHeight={eff.headerLineHeight}
							className="text-center"
							style={{ ...headerStyle, fontSize: `${pxToRem(eff.headerFont)}` }}
						/>
					);

					baseColumn.children = transformColumnsForAntd(
						col.children,
						t,
						isStackedColumn,
						isClusterColumn,
						columnWidths,
						labelFormat,
						dataSource,
						statTestConfig,
						scaledOverrides,
					);
				}

				if (isStackedColumn && isEvenGroup !== undefined) {
					baseColumn.onHeaderCell = () => headerCellProps;
				}

				return baseColumn;
			}

			// Leaf columns
			baseColumn.dataIndex = col.dataIndex;

			if (typeof col.title === "string") {
				if (col.dataIndex === "attribute") {
					const rowSpan = (col as any).rowSpan;

					baseColumn.title = (
						<TableEllipsisText
							text={t(col.title)}
							maxLines={eff.maxLines}
							lineHeight={eff.headerLineHeight}
							className="text-center"
							style={{
								fontSize: `${pxToRem(eff.headerFont)}`,
								fontWeight: typographyConfig.fontWeight.header,
								color: typographyConfig.color.attribute,
								textAlign: alignmentConfig.horizontal.attribute,
							}}
						/>
					);

					// Apply rowSpan to span all header rows (Row 1, 2, 3)
					if (rowSpan && rowSpan > 1) {
						baseColumn.onHeaderCell = () => createHeaderCellProps(columnType, isEvenGroup, rowSpan);
					}
				} else if (col.dataIndex === "wave") {
					if (isStackedColumn || isClusterColumn) {
						return null;
					}

					const rowSpan = (col as any).rowSpan;

					baseColumn.title = (
						<TableEllipsisText
							text={t(col.title)}
							maxLines={eff.maxLines}
							lineHeight={eff.headerLineHeight}
							className="text-center"
							style={{
								fontSize: `${pxToRem(eff.headerFont)}`,
								fontWeight: typographyConfig.fontWeight.header,
								color: typographyConfig.color.header,
							}}
						/>
					);

					// Apply rowSpan to span all header rows
					if (rowSpan && rowSpan > 1) {
						baseColumn.onHeaderCell = () => createHeaderCellProps(columnType, isEvenGroup, rowSpan);
					}
				} else if (col.title === "flatData") {
					// FLAT DATA: Show only n-size
					const nSizeData = (col as any)._nSizeData;
					const isFlatData = (col as any)._isFlatData;

					if (isFlatData && nSizeData) {
						const minColor = tableConfig.getNSizeColor(nSizeData.minValue);
						const maxColor = tableConfig.getNSizeColor(nSizeData.maxValue);
						const nSizeStyle = tableConfig.getNSizeRowStyle();

						baseColumn.title = (
							<div style={nSizeStyle}>
								<NSizeDisplay
									minValue={nSizeData.minValue}
									maxValue={nSizeData.maxValue}
									minColor={minColor}
									maxColor={maxColor}
									typographyConfig={typographyConfig}
									scaledFontSize={scaledOverrides?.nSizeFontSize}
								/>
							</div>
						);
					} else {
						const headerStyle = tableConfig.getHeaderStyle(0, true);
						baseColumn.title = (
							<TableEllipsisText
								text="Data"
								maxLines={eff.maxLines}
								lineHeight={eff.headerLineHeight}
								className="text-center"
								style={{ ...headerStyle, fontSize: `${pxToRem(eff.headerFont)}` }}
							/>
						);
					}
				} else {
					// Check if this is flat data with n-size (for h_cluster_bar)
					const nSizeData = (col as any)._nSizeData;
					const isFlatData = (col as any)._isFlatData;

					if (isFlatData && nSizeData) {
						// h_cluster_bar flat data - show ONLY n-size (no column name above it)
						const minColor = tableConfig.getNSizeColor(nSizeData.minValue);
						const maxColor = tableConfig.getNSizeColor(nSizeData.maxValue);

						baseColumn.title = (
							<NSizeDisplay
								minValue={nSizeData.minValue}
								maxValue={nSizeData.maxValue}
								minColor={minColor}
								maxColor={maxColor}
								typographyConfig={typographyConfig}
								scaledFontSize={scaledOverrides?.nSizeFontSize}
							/>
						);
					} else {
						const headerStyle = tableConfig.getHeaderStyle(0, true);
						baseColumn.title = (
							<TableEllipsisText
								text={t(col.title)}
								maxLines={eff.maxLines}
								lineHeight={eff.headerLineHeight}
								className="text-center"
								style={{ ...headerStyle, fontSize: `${pxToRem(eff.headerFont)}` }}
							/>
						);
					}
				}
			} else {
				baseColumn.title = col.title;
			}

			// Custom render function with config-driven styling
			baseColumn.render = (value: any, record: TableRow, index: number) => {
				const cellStyle: React.CSSProperties = {
					fontSize: `${pxToRem(eff.dataFont)}`,
					fontWeight: typographyConfig.fontWeight.data,
					padding: `${eff.padV}px ${eff.padH}px`,
				};

				// Handle attribute column with rowspan
				if (col.dataIndex === "attribute") {
					if (isStackedColumn || isClusterColumn) {
						return (
							<TableEllipsisText
								text={value ?? "-"}
								maxLines={eff.maxLines}
								lineHeight={eff.headerLineHeight}
								style={{
									...cellStyle,
									fontWeight: typographyConfig.fontWeight.attribute,
									color: typographyConfig.color.attribute,
								}}
							/>
						);
					}

					const rowSpan = record.attributeRowSpan ?? 1;

					if (rowSpan === 0) {
						return {
							children: null,
							props: { rowSpan: 0 },
						};
					}

					return {
						children: (
							<TableEllipsisText
								text={value ?? "-"}
								maxLines={eff.maxLines}
								lineHeight={eff.headerLineHeight}
								style={{
									...cellStyle,
									fontWeight: typographyConfig.fontWeight.attribute,
									color: typographyConfig.color.attribute,
								}}
							/>
						),
						props: { rowSpan },
					};
				}

				// Handle wave column
				if (col.dataIndex === "wave") {
					return (
						<TableEllipsisText
							text={value ?? "-"}
							maxLines={eff.maxLines}
							lineHeight={eff.headerLineHeight}
							style={{
								...cellStyle,
								color: typographyConfig.color.default,
							}}
						/>
					);
				}

				// Handle data cells - use labelFormat for formatting
				const cellNSize = record._nSizes?.[col.dataIndex as string];
				const cellNSizeStatusLabel = cellNSize !== undefined ? getNSizeStatusLabel(cellNSize) : null;

				if (col.isPercentage !== undefined) {
					if (cellNSizeStatusLabel !== null) {
						const isNoData = cellNSizeStatusLabel === NSizeLabelText.NoData;
						const labelSpan = (
							<span
								style={{
									...cellStyle,
									color: SUPPRESSED_VALUE_COLOR,
								}}>
								{isNoData ? SUPPRESSED_VALUE_PLACEHOLDER : cellNSizeStatusLabel}
							</span>
						);
						return isNoData ? (
							<Tooltip title="No data" placement="top" getPopupContainer={(triggerNode) => triggerNode.closest(".ant-table-body") || document.body}>
								{labelSpan}
							</Tooltip>
						) : (
							labelSpan
						);
					}
					if (value === null || value === undefined) {
						return (
							<Tooltip title="No data" placement="top" getPopupContainer={(triggerNode) => triggerNode.closest(".ant-table-body") || document.body}>
								<span
									style={{
										...cellStyle,
										color: SUPPRESSED_VALUE_COLOR,
									}}>
									{SUPPRESSED_VALUE_PLACEHOLDER}
								</span>
							</Tooltip>
						);
					}

					const statMarkerConfig = tableConfig.getStatMarkerConfig();
					const markerContent = record._statMarkers?.[col.dataIndex as string];

					const formattedValue = (
						<span
							style={{
								...cellStyle,
								color: typographyConfig.color.default,
							}}>
							{formatValue(value, labelFormat || "##")}
						</span>
					);

					if (statMarkerConfig.enabled && markerContent && statTestConfig) {
						return (
							<StatMarkerDisplay
								value={formattedValue}
								statMarkerObj={markerContent as IStatMarker}
								statTestConfig={statTestConfig}
								position={statMarkerConfig.position}
								spacing={statMarkerConfig.spacing}
								maxLines={statMarkerConfig.maxLines}
								maxWidth={statMarkerConfig.maxWidth}
							/>
						);
					}

					return formattedValue;
				}

				// Default - non-percentage numerical cells
				{
					const statMarkerConfig = tableConfig.getStatMarkerConfig();
					const markerContent = record._statMarkers?.[col.dataIndex as string];

					if (cellNSizeStatusLabel !== null) {
						const isNoData = cellNSizeStatusLabel === NSizeLabelText.NoData;
						const labelSpan = (
							<span
								style={{
									...cellStyle,
									color: SUPPRESSED_VALUE_COLOR,
								}}>
								{isNoData ? SUPPRESSED_VALUE_PLACEHOLDER : cellNSizeStatusLabel}
							</span>
						);
						return isNoData ? (
							<Tooltip title="No data" placement="top" getPopupContainer={(triggerNode) => triggerNode.closest(".ant-table-body") || document.body}>
								{labelSpan}
							</Tooltip>
						) : (
							labelSpan
						);
					}

					const isMissing = value === null || value === undefined;
					const defaultValueSpan = (
						<span
							style={{
								...cellStyle,
								color: isMissing ? SUPPRESSED_VALUE_COLOR : typographyConfig.color.default,
							}}>
							{value ?? SUPPRESSED_VALUE_PLACEHOLDER}
						</span>
					);
					const defaultValue = isMissing ? (
						<Tooltip title="No data" placement="top" getPopupContainer={(triggerNode) => triggerNode.closest(".ant-table-body") || document.body}>
							{defaultValueSpan}
						</Tooltip>
					) : (
						defaultValueSpan
					);

					if (statMarkerConfig.enabled && markerContent && statTestConfig) {
						return (
							<StatMarkerDisplay
								value={defaultValue}
								statMarkerObj={markerContent as IStatMarker}
								statTestConfig={statTestConfig}
								position={statMarkerConfig.position}
								spacing={statMarkerConfig.spacing}
								maxLines={statMarkerConfig.maxLines}
								maxWidth={statMarkerConfig.maxWidth}
							/>
						);
					}

					return defaultValue;
				}
			};

			return baseColumn;
		})
		.filter(Boolean);
};

/* =====================================
	 RAW COLUMNS HELPER
	 ===================================== */

/**
 * Derive the raw ColumnConfig array for the given chart type..
 */
const getRawColumns = (
	chartData: any,
	chartType: string,
	filterHierarchy?: IHierarchyDef[],
	seriesOrder?: string[],
): ColumnConfig[] => {
	switch (chartType) {
		case "h_cluster_bar":
			return getHClusterBarChartColumns(chartData, chartType, filterHierarchy);
		case "h_stacked_bar":
			return getHStackedBarChartColumns(chartData, chartType, filterHierarchy, seriesOrder);
		case "v_stacked_column":
		case "v_cluster_column":
			return getVStackedBarChartColumns(chartData, chartType, filterHierarchy, seriesOrder);
		default:
			return getHClusterBarChartColumns(chartData, chartType, filterHierarchy);
	}
};

const ChartTabularView: React.FC<ChartTabularViewProps> = (props) => {
	const { chartData, attributes = [], chartType = "h_cluster_bar", tableContainerHeight, parentContainerWidth, labelFormat, statTestConfig, scaledOverrides, filterHierarchy, seriesOrder } = props;
	const { t } = useTranslation();

	// Refs for container and table header
	const wrapperRef = useRef<HTMLDivElement>(null);
	const tableHeaderRef = useRef<HTMLElement | null>(null);
	const [containerWidth, setContainerWidth] = useState<number>(0);
	const [tableHeaderHeight, setTableHeaderHeight] = useState<number>(120); // Default fallback

	// Get table configuration
	const config = tableConfig.getConfig();
	const bordersConfig = config.borders;
	const colorsConfig = config.colors;
	const typographyConfig = config.typography;
	const targetVisibleColumns = config.visibleColumns?.target || 10; // Get target from config with fallback
	const fallback1TargetVisibleColumns = config.visibleColumns?.fallbacktarget1 || 5;
	const fallback2TargetVisibleColumns = config.visibleColumns?.fallbacktarget2 || 8;

	const isStackedColumn = useMemo(() => {
		return chartType === "v_stacked_column";
	}, [chartType]);

	const isClusterColumn = useMemo(() => {
		return chartType === "v_cluster_column";
	}, [chartType]);

	const rawColumns = useMemo((): ColumnConfig[] => {
		if (!chartData) return [];
		return getRawColumns(chartData, chartType, filterHierarchy, seriesOrder);
	}, [chartData, chartType, filterHierarchy, seriesOrder]);

	const titlesAboveNSize = useMemo((): string[] => {
		return extractLeafTitlesAboveNSize(rawColumns);
	}, [rawColumns]);

	const dataSource = useMemo((): TableRow[] => {
		if (!chartData) return [];
		switch (chartType) {
			case "h_cluster_bar":
				return getHClusterBarChartTableData(chartData, filterHierarchy);
			case "h_stacked_bar":
				return getHStackedBarChartTableData(chartData, filterHierarchy, seriesOrder);
			case "v_stacked_column":
			case "v_cluster_column":
				return getVStackedBarChartTableData(chartData, filterHierarchy, seriesOrder);
			default:
				return getHClusterBarChartTableData(chartData, filterHierarchy);
		}
	}, [chartData, chartType, filterHierarchy, seriesOrder]);

	const waveNamesKey = useMemo((): string => {
		return extractWaveNamesFromDataSource(dataSource).join("||");
	}, [dataSource]);

	const waveNames = useMemo((): string[] => {
		return waveNamesKey ? waveNamesKey.split("||").filter(Boolean) : [];
	}, [waveNamesKey]);

	/* =====================================
		 COLUMNS TRANSFORM
		 ===================================== */

	const columns = useMemo((): ColumnsType<TableRow> => {
		if (!chartData || !rawColumns.length) return [];

		const widthMetadata = (rawColumns as any)._widthMetadata;
		const hasWave = rawColumns.some((c) => c.dataIndex === "wave");
		const hasAttribute = rawColumns.some((c) => c.dataIndex === "attribute");

		let columnWidths: { attribute: number; wave: number; numerical: number } | undefined;

		if (widthMetadata) {
			const dimensions = calculateTableDimensions(
				widthMetadata.totalColumns,
				hasWave,
				hasAttribute,
				attributes,
				waveNames,
				targetVisibleColumns,
				containerWidth,
				parentContainerWidth ?? 0,
				isStackedColumn || isClusterColumn,
				titlesAboveNSize,
				fallback1TargetVisibleColumns,
				fallback2TargetVisibleColumns,
				scaledOverrides,
			);
			columnWidths = dimensions.columnWidths;
		}

		return transformColumnsForAntd(rawColumns, t, isStackedColumn, isClusterColumn, columnWidths, labelFormat, dataSource, statTestConfig, scaledOverrides);
	}, [
		chartData,
		rawColumns,
		t,
		isStackedColumn,
		isClusterColumn,
		attributes,
		waveNames,
		titlesAboveNSize,
		targetVisibleColumns,
		containerWidth,
		parentContainerWidth,
		labelFormat,
		dataSource,
		fallback1TargetVisibleColumns,
		fallback2TargetVisibleColumns,
		scaledOverrides,
	]);

	// Measure table header height - initial measurement
	useEffect(() => {
		const measureHeaderHeight = () => {
			if (wrapperRef.current) {
				const headerElement = wrapperRef.current.querySelector(".ant-table-thead") as HTMLElement;
				if (headerElement) {
					const height = headerElement.offsetHeight;
					setTableHeaderHeight(height);
					tableHeaderRef.current = headerElement;
				}
			}
		};

		// Initial measurement - use a small delay to ensure table is rendered
		const timer = setTimeout(measureHeaderHeight, 100);

		return () => clearTimeout(timer);
	}, [dataSource, columns]);

	// Measure container width and table header height on resize/zoom
	useEffect(() => {
		let debouncedUpdate: ReturnType<typeof debounce> | null = null;
		let resizeObserver: ResizeObserver | null = null;

		const updateDimensions = () => {
			if (wrapperRef.current) {
				// Update container width
				const width = wrapperRef.current.offsetWidth;
				setContainerWidth(width);

				// Update table header height
				const headerElement = wrapperRef.current.querySelector(".ant-table-thead") as HTMLElement;
				if (headerElement) {
					const height = headerElement.offsetHeight;
					setTableHeaderHeight(height);
				}
			}
		};

		// Initial measurement (immediate, no debounce)
		updateDimensions();

		// Create debounced version for resize events (150ms delay)
		debouncedUpdate = debounce(updateDimensions, 150);

		// Listen for window resize
		window.addEventListener("resize", debouncedUpdate);

		// Use ResizeObserver for accurate detection including zoom
		resizeObserver = new ResizeObserver(() => {
			if (debouncedUpdate) {
				debouncedUpdate();
			}
		});

		if (wrapperRef.current) {
			resizeObserver.observe(wrapperRef.current);
		}

		// Also observe the table header specifically for height changes
		if (tableHeaderRef.current) {
			resizeObserver.observe(tableHeaderRef.current);
		}

		return () => {
			if (debouncedUpdate) {
				window.removeEventListener("resize", debouncedUpdate);
				debouncedUpdate.cancel();
			}
			if (resizeObserver) {
				resizeObserver.disconnect();
			}
		};
	}, [dataSource, columns]);

	// Calculate table dimensions
	const tableDimensions = useMemo(() => {
		if (!chartData) {
			// Default dimensions when no data
			const defaultAttributeWidth = calculateAttributeColumnWidth(attributes, isStackedColumn || isClusterColumn, parentContainerWidth ?? 0);
			let defaultWaveWidth = 0;
			let defaultNumericalWidth = config.columns.numerical.minWidth;

			if (containerWidth > 0) {
				const availableWidth = containerWidth - defaultAttributeWidth;
				const columnWidth = availableWidth / 11;
				defaultWaveWidth = Math.max(columnWidth, 50);
				defaultNumericalWidth = Math.max(columnWidth, 50);
			} else {
				defaultWaveWidth = config.columns.wave.minWidth;
			}

			const defaultScrollX = defaultAttributeWidth + defaultWaveWidth + defaultNumericalWidth * targetVisibleColumns;
			return {
				wrapperWidth: `${defaultScrollX}px`,
				scrollX: defaultScrollX,
			};
		}

		const widthMetadata = (rawColumns as any)._widthMetadata;

		if (!widthMetadata) {
			const defaultAttributeWidth = calculateAttributeColumnWidth(attributes, isStackedColumn || isClusterColumn);
			let defaultWaveWidth = 0;
			let defaultNumericalWidth = config.columns.numerical.minWidth;

			if (containerWidth > 0) {
				const availableWidth = containerWidth - defaultAttributeWidth;
				const columnWidth = availableWidth / 11;
				defaultWaveWidth = Math.max(columnWidth, 50);
				defaultNumericalWidth = Math.max(columnWidth, 50);
			} else {
				defaultWaveWidth = config.columns.wave.minWidth;
			}

			const defaultScrollX = defaultAttributeWidth + defaultWaveWidth + defaultNumericalWidth * targetVisibleColumns;
			return {
				wrapperWidth: `${defaultScrollX}px`,
				scrollX: defaultScrollX,
			};
		}

		const hasWave = rawColumns.some((c) => c.dataIndex === "wave");
		const hasAttribute = rawColumns.some((c) => c.dataIndex === "attribute");

		return calculateTableDimensions(
			widthMetadata.totalColumns,
			hasWave,
			hasAttribute,
			attributes,
			waveNames,
			targetVisibleColumns,
			containerWidth,
			parentContainerWidth ?? 0,
			isStackedColumn || isClusterColumn,
			titlesAboveNSize,
			fallback1TargetVisibleColumns,
			fallback2TargetVisibleColumns,
			scaledOverrides,
		);
	}, [
		chartData,
		rawColumns,
		isStackedColumn,
		isClusterColumn,
		config,
		attributes,
		targetVisibleColumns,
		containerWidth,
		parentContainerWidth,
		waveNames,
		titlesAboveNSize,
		fallback1TargetVisibleColumns,
		fallback2TargetVisibleColumns,
	]);

	/**
	 * POST-RENDER HACK: Force header <table> width to exactly match body <table> width.
	 * AntD creates two separate <table> elements for sticky-header scroll.
	 * Its internal scrollbar-compensation cell + min-width logic can cause
	 * a 3–5px mismatch. This effect runs after every render that changes
	 * columns/data and overwrites the header table's inline width.
	 */
	/**
	 * POST-RENDER FIX: Sync header <table> width with body <table> width.
	 *
	 * AntD's rc-table sets header table width = scroll.x + scrollbarWidth,
	 * and adds an extra <col> in the header's <colgroup> for scrollbar compensation.
	 * This creates a ~10px mismatch causing independent header scroll.
	 *
	 * This observer continuously enforces:
	 * 1. Header table width/min-width = body table width
	 * 2. The extra scrollbar <col> in header colgroup is zeroed out
	 */
	/**
	 * POST-RENDER FIX: Sync header <table> width with body <table> width
	 * ONLY when the body has no vertical scrollbar.
	 *
	 * When vertical scroll exists, AntD's scrollbar compensation <col> and
	 * extra header width are correct — they prevent header/body column misalignment
	 * caused by the scrollbar consuming space in the body.
	 *
	 * When there is NO vertical scroll, the compensation creates a ~10px mismatch
	 * because there's no scrollbar to compensate for, causing the header to scroll
	 * independently.
	 */
	useEffect(() => {
		if (!wrapperRef.current) return;

		const wrapper = wrapperRef.current;
		let observer: MutationObserver | null = null;

		const syncHeaderToBody = () => {
			const bodyContainer = wrapper.querySelector(".ant-table-body") as HTMLElement;
			const bodyTable = wrapper.querySelector(".ant-table-body table") as HTMLTableElement;
			const headerTable = wrapper.querySelector(".ant-table-header table") as HTMLTableElement;

			if (!bodyContainer || !bodyTable || !headerTable) return;

			// Check if the body has a vertical scrollbar
			const hasVerticalScroll = bodyContainer.scrollHeight > bodyContainer.clientHeight;

			// If vertical scroll exists, AntD's compensation is correct — skip fix
			if (hasVerticalScroll) return;

			const bodyWidth = bodyTable.offsetWidth;

			// Override header table inline styles set by rc-table
			headerTable.style.width = `${bodyWidth}px`;
			headerTable.style.minWidth = `${bodyWidth}px`;
			headerTable.style.maxWidth = `${bodyWidth}px`;

			// Zero out the extra scrollbar <col> in header's colgroup
			const headerColGroup = headerTable.querySelector("colgroup");
			const bodyColGroup = bodyTable.querySelector("colgroup");
			if (headerColGroup && bodyColGroup) {
				const headerCols = headerColGroup.querySelectorAll("col");
				const bodyCols = bodyColGroup.querySelectorAll("col");

				// Header has one extra <col> for scrollbar — zero it only when no vertical scroll
				if (headerCols.length > bodyCols.length) {
					const scrollbarCol = headerCols[headerCols.length - 1];
					scrollbarCol.style.width = "0px";
					scrollbarCol.style.minWidth = "0px";
				}
			}
		};

		// Initial sync after AntD finishes layout
		const rafId = requestAnimationFrame(syncHeaderToBody);

		// Observe header table for AntD re-applying inline styles on scroll/resize
		const headerDiv = wrapper.querySelector(".ant-table-header");
		if (headerDiv) {
			observer = new MutationObserver(() => {
				requestAnimationFrame(syncHeaderToBody);
			});
			observer.observe(headerDiv, {
				attributes: true,
				subtree: true,
				attributeFilter: ["style"],
			});
		}

		return () => {
			cancelAnimationFrame(rafId);
			if (observer) observer.disconnect();
		};
	}, [columns, dataSource, tableDimensions, containerWidth]);

	const tableScrollHeight = useMemo(() => {
		if (tableContainerHeight && tableContainerHeight > 0) {
			const calculatedHeight = tableContainerHeight - tableHeaderHeight - 5;
			return Math.max(calculatedHeight, 100);
		}
		return 100;
	}, [tableContainerHeight, tableHeaderHeight]);

	// Generate CSS variables from config
	const cssVariables = useMemo(() => {
		const borderColor = bordersConfig.color[bordersConfig.application.innerBorder];
		const outerBorderColor = bordersConfig.color[bordersConfig.application.outerBorder];
		const headerBorderColor = bordersConfig.color[bordersConfig.application.headerBorder];
		const borderWidth = bordersConfig.width[bordersConfig.application.innerBorder];
		const outerBorderWidth = bordersConfig.width[bordersConfig.application.outerBorder];
		const headerBorderWidth = bordersConfig.width[bordersConfig.application.headerBorder];

		const nSizeStyle = tableConfig.getNSizeRowStyle();

		return {
			"--table-border-color": borderColor,
			"--table-outer-border-color": outerBorderColor,
			"--table-header-border-color": headerBorderColor,
			"--table-border-width": `${borderWidth}px`,
			"--table-outer-border-width": `${outerBorderWidth}px`,
			"--table-header-border-width": `${headerBorderWidth}px`,
			"--table-header-bg": colorsConfig.background.header,
			"--table-even-column-bg": colorsConfig.background.evenColumn,
			"--table-odd-column-bg": colorsConfig.background.oddColumn,
			"--table-fixed-column-bg": colorsConfig.background.fixedColumn,
			"--table-font-size": scaledOverrides ? `${pxToRem(scaledOverrides.dataFontSize)}` : `${typographyConfig.fontSize.data}px`,
			"--table-line-height": typographyConfig.lineHeight,
			"--table-nsize-font-size": nSizeStyle.fontSize,
			"--table-nsize-line-height": nSizeStyle.lineHeight,
			"--table-nsize-font-weight": nSizeStyle.fontWeight,
			"--table-cell-padding-v": `${scaledOverrides?.cellPaddingV ?? 4}px`,
			"--table-cell-padding-h": `${scaledOverrides?.cellPaddingH ?? 8}px`,
		} as React.CSSProperties;
	}, [bordersConfig, colorsConfig, typographyConfig, scaledOverrides]);

	if (!chartData || dataSource.length === 0) {
		return (
			<div className="chart-tabular-view flex items-center justify-center p-8">
				<span
					style={{
						fontSize: `${config.typography.fontSize.base}px`,
						color: config.typography.color.tertiary,
					}}>
					{t("No data")}
				</span>
			</div>
		);
	}

	return (
		<div
			ref={wrapperRef}
			className="chart-tabular-view-wrapper"
			style={{
				...cssVariables,
				width: tableDimensions.wrapperWidth,
				maxWidth: "100%",
			}}>
			<Table
				columns={columns}
				dataSource={dataSource}
				pagination={false}
				className={`dashboard-chart-table ${isStackedColumn ? "column-based-table" : isClusterColumn ? "cluster-column-table" : "row-based-table"}`}
				locale={{ emptyText: t("No data") }}
				scroll={{
					x: tableDimensions.scrollX,
					y: tableScrollHeight,
				}}
				bordered
				size="small"
				tableLayout="fixed"
			/>
		</div>
	);
};

export default ChartTabularView;
